import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const ENTITIES = ["patient", "opd", "ipd", "billing"];

export default function CustomFieldsPage() {
  const [entity, setEntity] = useState("patient");
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState({ entity: "patient", label: "", key: "", type: "text", options: "", required: false, section: "Custom" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/custom-fields", { params: { entity } }).then((r) => setFields(r.data || []));
  useEffect(() => { load(); setForm((f) => ({ ...f, entity })); }, [entity]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        key: form.key || form.label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, ""),
        options: form.type === "select" ? form.options.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      await api.post("/custom-fields", payload);
      toast.success("Custom field added");
      setForm({ entity, label: "", key: "", type: "text", options: "", required: false, section: "Custom" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this field?")) return;
    try { await api.delete(`/custom-fields/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="cf-page">
      <PageHeader eyebrow="Administration" title="Custom fields builder"
        subtitle="Add hospital-specific fields to Patient, OPD, IPD or Billing forms — without code." />

      <Tabs value={entity} onValueChange={setEntity} className="mb-3">
        <TabsList className="bg-[var(--hms-paper)] rounded-sm border border-[var(--hms-border-2)]">
          {ENTITIES.map((e) => (
            <TabsTrigger key={e} value={e} data-testid={`cf-tab-${e}`} className="rounded-sm capitalize data-[state=active]:bg-white data-[state=active]:shadow-none">{e}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <form onSubmit={submit} className="hms-card p-4 lg:col-span-1 space-y-3" data-testid="cf-form">
          <div className="hms-section-title">Add field for <span className="text-[var(--hms-accent)]">{entity}</span></div>
          <Field label="Label" required value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="cf-label" />
          <Field label="Key (optional, auto)" value={form.key} onChange={(v) => setForm({ ...form, key: v })} testid="cf-key" />
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="cf-type" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["text", "number", "date", "select", "textarea"].map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {form.type === "select" && (
            <Field label="Options (comma separated)" value={form.options} onChange={(v) => setForm({ ...form, options: v })} testid="cf-options" />
          )}
          <Field label="Section" value={form.section} onChange={(v) => setForm({ ...form, section: v })} testid="cf-section" />
          <div className="flex items-center gap-2">
            <Switch checked={form.required} onCheckedChange={(v) => setForm({ ...form, required: v })} data-testid="cf-required" />
            <Label className="text-xs">Required</Label>
          </div>
          <Button type="submit" disabled={busy} data-testid="cf-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)] w-full">
            <Plus size={14} weight="bold" className="mr-1.5" /> {busy ? "Adding…" : "Add field"}
          </Button>
        </form>

        <div className="lg:col-span-2">
          {fields.length === 0 ? <EmptyState title={`No custom fields for ${entity}`} hint="Use the form on the left to add your first field." /> : (
            <div className="hms-card overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
                  <tr>
                    <th className="text-left px-4 py-2.5">Label</th>
                    <th className="text-left px-4 py-2.5">Key</th>
                    <th className="text-left px-4 py-2.5">Type</th>
                    <th className="text-left px-4 py-2.5">Required</th>
                    <th className="text-left px-4 py-2.5">Section</th>
                    <th className="text-right px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f) => (
                    <tr key={f.id} className="border-b border-[var(--hms-border)]" data-testid={`cf-row-${f.key}`}>
                      <td className="px-4 py-2.5 font-medium">{f.label}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{f.key}</td>
                      <td className="px-4 py-2.5">{f.type}</td>
                      <td className="px-4 py-2.5">{f.required ? "Yes" : "No"}</td>
                      <td className="px-4 py-2.5">{f.section}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Button variant="ghost" size="sm" onClick={() => del(f.id)} className="text-[var(--hms-accent)] rounded-sm" data-testid={`cf-del-${f.key}`}>
                          <Trash size={14} weight="bold" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}{required && " *"}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
    </div>
  );
}
